import React, { useContext, useEffect, useRef, useState } from 'react'
import {
    BeakerIcon,
    BriefcaseIcon,
    CpuChipIcon,
    CloudIcon,
    ShieldExclamationIcon,
    TruckIcon,
    UserIcon,
    UsersIcon,
    HandThumbUpIcon,
    RectangleStackIcon,
    RectangleGroupIcon,
    GlobeEuropeAfricaIcon,
    LanguageIcon,
    CircleStackIcon,
    TableCellsIcon,
    MapPinIcon,
    BuildingOfficeIcon,
    ExclamationTriangleIcon,
    Bars3Icon,
    CreditCardIcon
} from '@heroicons/react/24/outline'
import { Outlet, useLocation } from 'react-router-dom'
import AuthController from './controllers/AuthController'
import AppContext from './appContext'
import UserController from './controllers/UserController'
import AppState from './controllers/AppState'
import MeasurementUnit from './controllers/MeasurementUnit'
import PermissionEnum from './controllers/PermissionEnum'
import Axios from 'axios'
import { isLocalhost } from './controllers/helper'
import Sidebar from './components/Sidebar'

const signalR = require('@microsoft/signalr')

// Navigation organized into three sections
const managementNavigation = [
    {
        permission: PermissionEnum.ManagementLanguagePermission,
        name: 'language',
        href: '/languages',
        icon: LanguageIcon
    },
    {
        permission: PermissionEnum.ManagementClientsPermission,
        name: 'clients',
        href: '/clients',
        icon: UsersIcon
    },
    {
        permission: PermissionEnum.ManagementFarmsPermission,
        name: 'farms',
        href: '/farms',
        icon: CloudIcon
    },
    {
        permission: PermissionEnum.ManagementAgentPermission,
        name: 'agents',
        href: '/agents',
        icon: BriefcaseIcon
    },
    {
        permission: PermissionEnum.ManagementDistributorPermission,
        name: 'distributors',
        href: '/distributors',
        icon: TruckIcon
    },
    {
        permission: PermissionEnum.ManagementCategoryPermission,
        name: 'categories',
        href: '/categories',
        icon: RectangleStackIcon
    },
    {
        permission: PermissionEnum.ManagementRegions,
        name: 'regions',
        href: '/regions',
        icon: GlobeEuropeAfricaIcon
    },
    {
        permission: PermissionEnum.ManagementCropPermission,
        name: 'crops',
        href: '/crops',
        icon: CircleStackIcon
    },
    {
        permission: PermissionEnum.ManagementCultivarPermission,
        name: 'cultivars',
        href: '/cultivars',
        icon: TableCellsIcon
    },
    {
        permission: PermissionEnum.ManagementAddress,
        name: 'addresses',
        href: '/addresses',
        icon: MapPinIcon
    },
    {
        permission: PermissionEnum.ManagementRolesPermissions,
        name: 'roles',
        href: '/roles',
        icon: ShieldExclamationIcon
    },
    {
        permission: PermissionEnum.ManagementCompanyPermission,
        name: 'companies',
        href: '/companies',
        icon: BuildingOfficeIcon
    },
    {
        permission: PermissionEnum.ManagementUsersPermission,
        name: 'users',
        href: '/users',
        icon: UserIcon
    },
    {
        permission: PermissionEnum.ManagementUsersPermission,
        name: 'currencies',
        href: '/currencies',
        icon: CreditCardIcon
    },
    {
        permission: PermissionEnum.ManageErrors,
        name: 'errors',
        href: '/errors',
        icon: ExclamationTriangleIcon
    }
]

const fertiliserNavigation = [
    {
        permission: PermissionEnum.FertiliserProgramsPermission,
        name: 'programs',
        href: '/programs',
        icon: CpuChipIcon
    },
    {
        permission: PermissionEnum.FertiliserApprovalsPermission,
        name: 'approvals',
        href: '/approvals',
        icon: HandThumbUpIcon
    },
    {
        permission: PermissionEnum.FertiliserTemplatesPermission,
        name: 'templates',
        href: '/templates',
        icon: RectangleGroupIcon
    },
    {
        permission: PermissionEnum.FertiliserProductsPermission,
        name: 'products',
        href: '/products',
        icon: BeakerIcon
    }
]

const limingNavigation = [
    {
        permission: PermissionEnum.LimingProgramsPermission,
        name: 'liming_programs',
        href: '/liming/programs',
        icon: CpuChipIcon
    },
    {
        permission: PermissionEnum.LimingApprovalsPermission,
        name: 'liming_approvals',
        href: '/liming/approvals',
        icon: HandThumbUpIcon
    },
    {
        permission: PermissionEnum.LimingTemplatesPermission,
        name: 'liming_templates',
        href: '/liming/templates',
        icon: RectangleGroupIcon
    },
    {
        permission: PermissionEnum.LimingProductsPermission,
        name: 'liming_products',
        href: '/liming/products',
        icon: BeakerIcon
    }
]

function classNames (...classes: string[]): string {
    return classes.filter(Boolean).join(' ')
}


if (!isLocalhost()) {
    Axios.interceptors.response.use(undefined, error => {
        window.location.href = '/';
    });
}

function logout () {
    AuthController.logout().then(() => {
        window.location.href = '/'
    })
}

function setLanguage (setInitial: (state: AppState) => void, id: number) {
    UserController.setLangauge({
        id
    }).then(() => {
        AuthController.getAppState().then(resp => {
            // setInitial(resp.data)
            window.location.reload()
        })
    })
}

function setUnit (val: MeasurementUnit) {
    UserController.changeDisplayUnit({
        unit: val
    }).then(() => window.location.reload())
}
const signal = new signalR.HubConnectionBuilder()
    .withUrl('/messages')
    .withAutomaticReconnect()
    .build()

export default function App (props: { setInitial: (state: AppState) => void }) {
    const app = useContext(AppContext)
    const location = useLocation()
    const [isCollapsed, setIsCollapsed] = useState(false)
    const [mobileOpen, setMobileOpen] = useState(false)
    const [showPopup, setShopPopup] = useState(false)
    const [showUpdate, setShowUpdate] = useState(false)

    // Filter navigation items by permission
    const approvedManagement = managementNavigation.filter(item => app.hasPermission(item.permission))
    const approvedFertiliser = fertiliserNavigation.filter(item => app.hasPermission(item.permission))
    const approvedLiming = limingNavigation.filter(item => app.hasPermission(item.permission))

    // Create sections for sidebar (Fertiliser and Liming first, Management last)
    const sections = [
        {
            title: app.word('fertiliser'),
            items: approvedFertiliser.map(item => ({
                ...item,
                name: app.word(item.name)
            }))
        },
        {
            title: app.word('liming'),
            items: approvedLiming.map(item => ({
                ...item,
                name: app.word(item.name)
            }))
        },
        {
            title: app.word('management'),
            items: approvedManagement.map(item => ({
                ...item,
                name: app.word(item.name)
            }))
        }
    ].filter(section => section.items.length > 0)

    function windowClicked (event: MouseEvent) {
        setShopPopup(false)
    }

    useEffect(() => {
        signal.start().then(() => {
            signal.on('Version', (version: string) => {
                if (version !== app.initial.version) setShowUpdate(true)
            })
            signal.send('ClientVersion', {version: app.initial.version})
        })

        window.addEventListener('click', windowClicked)
        return () => {
            window.removeEventListener('click', windowClicked)
        }
    }, [])

    const logo = (
        <div className="relative">
            <img className="w-auto" alt="logo" src="/sprites/logo.png"/>
            <div className="text-[0.675rem] mt-1 flex max-w-[170px]" title={`v${app.initial.version}`}>
                <span className="px-1 bg-gray-700 rounded-l-lg text-gray-300 flex-shrink-0">Fertiliser</span>
                <span className="px-1 bg-primary-600 rounded-r-lg text-white truncate min-w-0">v{app.initial.version}</span>
            </div>
        </div>
    )

    const userMenu = (
        <div className="relative">
            <div onClick={event => {
                setShopPopup(!showPopup)
                event.stopPropagation()
            }}>
                {!isCollapsed ? (
                    <div className="flex items-center justify-between cursor-pointer hover:bg-gray-700 rounded-md p-2">
                        <div className="text-white text-sm truncate">{app.initial.userDisplay}</div>
                        <Bars3Icon className="text-white h-6 w-6 ml-2 flex-shrink-0"/>
                    </div>
                ) : (
                    <div className="flex justify-center cursor-pointer hover:bg-gray-700 rounded-md p-2">
                        <Bars3Icon className="text-white h-6 w-6"/>
                    </div>
                )}
            </div>

            {showPopup && (
                <div
                    className={classNames(
                        "absolute bg-white border rounded shadow-lg z-[70] w-[200px]",
                        isCollapsed ? "left-full ml-2 bottom-0" : "bottom-full mb-2 right-0"
                    )}
                    onClick={e => e.stopPropagation()}
                >
                    <ul className="text-left">
                        <div className="p-3">
                            <label htmlFor="language" className="block text-sm font-medium text-gray-700">{app.word('language')}</label>
                            <select
                                id="language"
                                className="w-full mt-1 block rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                value={app.initial.languageId}
                                onChange={e => setLanguage(props.setInitial, parseInt(e.target.value))}
                            >
                                {app.initial.languages.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                        </div>

                        <div className="p-3">
                            <label htmlFor="unit" className="block text-sm font-medium text-gray-700">{app.word('unit')}</label>
                            <select
                                id="unit"
                                className="w-full mt-1 block rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                value={app.initial.system}
                                onChange={e => setUnit(e.target.value as MeasurementUnit)}
                            >
                                <option value={MeasurementUnit.METRIC}>{app.word('metric')}</option>
                                <option value={MeasurementUnit.IMPERIAL}>{app.word('imperial')}</option>
                            </select>
                        </div>

                        <li className="border-t p-3 cursor-pointer hover:bg-primary-500 hover:text-white" onClick={logout}>
                            {app.word('logout')}
                        </li>
                    </ul>
                </div>
            )}
        </div>
    )

    return (
        <div>
            {/* Development environment indicator */}
            {app.initial.isDev && (
                <div className='w-full border-b-2 bg-red-500 px-10 py-2'>
                    <div className='flex items-center justify-center text-xl text-white'>
                        This is a testing environment.
                    </div>
                </div>
            )}

            {/* Version update notification */}
            {showUpdate && (
                <div className='sticky top-0 z-50 w-full h-[5rem] border-8 bg-primary-600 px-10'>
                    <div className='flex w-full h-[4rem] items-center justify-between'>
                        <div className='text-primary text-xl text-white'>
                            {app.word('new_version_available') + ". " + app.word('save_and_update')}.
                        </div>
                        <div className='btn bg-primary' onClick={() => {
                            setShowUpdate(false)
                            window.location.reload()
                        }}>
                            {app.word('update')}
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile menu button */}
            <div className="lg:hidden fixed top-4 left-4 z-50">
                <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-md p-2 text-white bg-gray-800 hover:bg-gray-700"
                    onClick={() => setMobileOpen(true)}
                >
                    <span className="sr-only">Open sidebar</span>
                    <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </button>
            </div>

            {/* Sidebar */}
            <Sidebar
                sections={sections}
                isCollapsed={isCollapsed}
                onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
                mobileOpen={mobileOpen}
                onMobileClose={() => setMobileOpen(false)}
                logo={logo}
                userMenu={userMenu}
            />

            {/* Main content */}
            <div className={classNames(
                'transition-all duration-300',
                isCollapsed ? 'lg:pl-28' : 'lg:pl-56'
            )}>
                <Outlet/>
            </div>
        </div>
    )
}
